import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { eur } from "@/lib/constants";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const SUPPORT_OPTIONS = [
  "Carte", "Flyer", "Faire-part", "Invitation", "Carte postale",
  "Marque-page", "Affiche", "Réglette", "Fiche", "Support pédagogique",
  "Dépliant", "Autre",
];

const FOLD_TYPES = [
  { key: "simple", label: "Pli simple" },
  { key: "roule", label: "Pli roulé" },
  { key: "accordeon", label: "Accordéon" },
  { key: "fenetre", label: "Pli fenêtre" },
  { key: "unknown", label: "Je ne sais pas" },
  { key: "autre", label: "Autre" },
];

const DESTINATIONS = [
  { key: "imprimeur", label: "Imprimeur professionnel" },
  { key: "perso", label: "Impression personnelle" },
  { key: "digital", label: "Utilisation numérique" },
  { key: "both", label: "Impression + numérique" },
  { key: "unknown", label: "Je ne sais pas encore" },
];

const BASE_PRICE = 39;
const EXTRA_PRICE = 19;

function computeFaces({ folded, folds, recto_verso }) {
  if (folded) {
    const perSide = Math.max(1, (Number(folds) || 0) + 1);
    return perSide * (recto_verso ? 2 : 1);
  }
  return recto_verso ? 2 : 1;
}
function computePrice(faces) {
  const n = Math.max(1, faces || 1);
  return BASE_PRICE + (n - 1) * EXTRA_PRICE;
}

const STEPS = [
  { key: "support", label: "Votre support" },
  { key: "brief", label: "Votre projet" },
  { key: "files", label: "Vos fichiers" },
  { key: "format", label: "Format & structure" },
  { key: "print", label: "Et pour l'impression ?" },
  { key: "recap", label: "Récapitulatif" },
  { key: "confirm", label: "Confirmation" },
];

const PRINT_QUANTITIES = ["100", "250", "500", "1000", "autre"];

export default function CreationConfigurator() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);

  // 1 — Support
  const [support, setSupport] = useState("");
  const [supportOther, setSupportOther] = useState("");

  // 2 — Brief
  const [brief, setBrief] = useState("");
  const [theme, setTheme] = useState("");
  const [colors, setColors] = useState("");
  const [must, setMust] = useState("");
  const [avoid, setAvoid] = useState("");
  const [inspirations, setInspirations] = useState("");
  const [deadline, setDeadline] = useState("");

  // 3 — Files
  const [files, setFiles] = useState([]);
  const fileIds = useMemo(() => files.map((f) => f.id).filter(Boolean), [files]);

  // 4 — Format & structure
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [dimUnknown, setDimUnknown] = useState(false);
  const [rectoVerso, setRectoVerso] = useState(false);
  const [folded, setFolded] = useState(false);
  const [folds, setFolds] = useState(1);
  const [foldType, setFoldType] = useState("simple");
  const [closedW, setClosedW] = useState("");
  const [closedH, setClosedH] = useState("");
  const [destination, setDestination] = useState("imprimeur");
  const [printerSpecs, setPrinterSpecs] = useState("");
  const [specsFiles, setSpecsFiles] = useState([]);
  const specsFileIds = useMemo(() => specsFiles.map((f) => f.id).filter(Boolean), [specsFiles]);

  // 5 — Impression & livraison (facultatif)
  const [printOption, setPrintOption] = useState("files_only");
  const [printQuantity, setPrintQuantity] = useState("");
  const [printQuantityOther, setPrintQuantityOther] = useState("");
  const [printRequirements, setPrintRequirements] = useState("");

  const faces = useMemo(() => computeFaces({ folded, folds, recto_verso: rectoVerso }), [folded, folds, rectoVerso]);
  const price = computePrice(faces);

  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        support: !support ? "" : (support === "Autre" ? "autre" : support.toLowerCase()),
        support_other: support === "Autre" ? supportOther.trim() : "",
        brief: brief.trim(),
        theme: theme.trim(),
        colors: colors.trim(),
        must_have: must.trim(),
        must_avoid: avoid.trim(),
        inspirations: inspirations.trim(),
        deadline: deadline.trim(),
        width_mm: dimUnknown ? null : (widthMm ? Number(widthMm) : null),
        height_mm: dimUnknown ? null : (heightMm ? Number(heightMm) : null),
        dim_unknown: !!dimUnknown,
        recto_verso: !!rectoVerso,
        folded: !!folded,
        folds: folded ? Number(folds) : 0,
        fold_type: folded ? foldType : "",
        closed_w_mm: folded && closedW ? Number(closedW) : null,
        closed_h_mm: folded && closedH ? Number(closedH) : null,
        destination,
        printer_specs_text: printerSpecs.trim(),
        specs_file_ids: specsFileIds,
        file_ids: fileIds,
        print_option: printOption,
        print_quantity: printOption === "print_delivery" ? printQuantity : "",
        print_quantity_other: printOption === "print_delivery" && printQuantity === "autre" ? printQuantityOther.trim() : "",
        print_requirements: printOption === "print_delivery" ? printRequirements.trim() : "",
        confirm: true,
      };
      const { data } = await api.post("/creation/order", payload);
      toast.success("Votre demande a bien été enregistrée.");
      nav(`/espace/commande/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) {
      // Bug #6 — pas de support par défaut : bloquer tant qu'aucun choix
      if (!support) return false;
      return support !== "Autre" || supportOther.trim().length > 0;
    }
    if (step === 1) return brief.trim().length >= 8;
    if (step === 3) return dimUnknown || (widthMm && heightMm);
    if (step === 4) {
      if (printOption === "print_delivery") {
        if (!printQuantity) return false;
        if (printQuantity === "autre" && !printQuantityOther.trim()) return false;
      }
      return true;
    }
    return true;
  };

  return (
    <div className="cs-cream min-h-[85vh] py-14" data-testid="page-creation-configurator">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <Link to="/creer-mon-support" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Retour
        </Link>
        <div className="mt-4">
          <div className="cs-eyebrow text-[#0E7C90]">Création graphique · Configurateur</div>
          <h1 className="cs-h1 mt-2 text-4xl lg:text-5xl">Créer mon support</h1>
        </div>

        {/* Progress */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="steps-bar">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] border ${i === step ? "bg-black text-[#FAF8F5] border-black" : i < step ? "border-black/60 text-black" : "border-black/20 text-neutral-500"}`}>
              {i + 1} — {s.label}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-10">
          <div>
            {step === 0 && (
              <div className="cs-card p-6" data-testid="step-support">
                <div className="cs-eyebrow text-neutral-500">Étape 1 · Votre support</div>
                <h2 className="cs-h3 mt-2 text-2xl">Quel support souhaitez-vous créer ?</h2>
                {!support && (
                  <div role="alert" aria-live="polite" className="mt-3 text-[13px] font-mono text-neutral-600" data-testid="support-hint">
                    Aucun support choisi — sélectionnez un support pour continuer.
                  </div>
                )}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {SUPPORT_OPTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => setSupport(s)} data-testid={`support-${s}`}
                      className={`border px-4 py-4 text-[15px] font-serif transition-colors ${support === s ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white hover:border-black/50"}`}>
                      {s === "Carte" ? "Carte / Carte de visite" : s}
                    </button>
                  ))}
                </div>
                {support === "Autre" && (
                  <label className="mt-4 block">
                    <span className="cs-eyebrow text-neutral-500">Précisez</span>
                    <input value={supportOther} onChange={(e) => setSupportOther(e.target.value)} className="cs-input mt-2 w-full" data-testid="support-other" placeholder="Ex. étiquette autocollante, kakémono, sticker…" />
                  </label>
                )}
                <div className="mt-6 border border-amber-400 bg-amber-50/60 p-4 flex items-start gap-3" data-testid="multipage-warning">
                  <AlertTriangle size={18} className="text-amber-800 shrink-0 mt-0.5" />
                  <div className="text-[13px] text-neutral-800 leading-relaxed">
                    <strong>Un support, pas un document multipage.</strong> Cette prestation concerne un support unique
                    (recto, recto-verso, ou plié). Pour un livre, livret, brochure, catalogue, magazine ou revue,
                    utilisez la rubrique <Link to="/services/edition" className="cs-link-underline">Mise en page & édition →</Link>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="cs-card p-6 space-y-4" data-testid="step-brief">
                <div className="cs-eyebrow text-neutral-500">Étape 2 · Votre projet</div>
                <h2 className="cs-h3 text-2xl">Imaginez votre projet avec nous</h2>
                <p className="text-sm text-neutral-600">Décrivez librement ce que vous souhaitez obtenir. Une idée, une ambiance ou quelques mots suffisent pour commencer.</p>
                <textarea rows={6} value={brief} onChange={(e) => setBrief(e.target.value)} className="cs-input w-full resize-none" data-testid="brief"
                  placeholder="Ex. : Je voudrais un faire-part de mariage élégant et chaleureux, avec une ambiance champêtre, des fleurs très fines et des tons naturels…" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block"><span className="cs-eyebrow text-neutral-500">Thème / univers</span>
                    <input value={theme} onChange={(e) => setTheme(e.target.value)} className="cs-input mt-2 w-full" data-testid="theme" placeholder="Facultatif" /></label>
                  <label className="block"><span className="cs-eyebrow text-neutral-500">Couleurs préférées</span>
                    <input value={colors} onChange={(e) => setColors(e.target.value)} className="cs-input mt-2 w-full" data-testid="colors" placeholder="Facultatif" /></label>
                  <label className="block sm:col-span-2"><span className="cs-eyebrow text-neutral-500">Éléments à faire apparaître</span>
                    <input value={must} onChange={(e) => setMust(e.target.value)} className="cs-input mt-2 w-full" data-testid="must" placeholder="Facultatif" /></label>
                  <label className="block sm:col-span-2"><span className="cs-eyebrow text-neutral-500">Éléments à éviter</span>
                    <input value={avoid} onChange={(e) => setAvoid(e.target.value)} className="cs-input mt-2 w-full" data-testid="avoid" placeholder="Facultatif" /></label>
                  <label className="block sm:col-span-2"><span className="cs-eyebrow text-neutral-500">Inspirations, références</span>
                    <textarea rows={2} value={inspirations} onChange={(e) => setInspirations(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="inspirations" placeholder="Facultatif — vous pourrez aussi joindre des fichiers à l'étape suivante" /></label>
                  <label className="block sm:col-span-2"><span className="cs-eyebrow text-neutral-500">Délai souhaité (indicatif)</span>
                    <input value={deadline} onChange={(e) => setDeadline(e.target.value)} className="cs-input mt-2 w-full" data-testid="deadline" placeholder="Ex. avant le 30/06 pour un envoi imprimeur mi-juillet" /></label>
                </div>
                <p className="text-[11px] font-mono text-neutral-500">Aucun délai n'est garanti automatiquement — Charbonier Studio vous confirme la faisabilité.</p>
              </div>
            )}

            {step === 2 && (
              <div className="cs-card p-6 space-y-4" data-testid="step-files">
                <div className="cs-eyebrow text-neutral-500">Étape 3 · Vos fichiers</div>
                <h2 className="cs-h3 text-2xl">Envoyez ce qui peut nous aider</h2>
                <p className="text-sm text-neutral-600">
                  Vous pouvez envoyer textes, photos, logo, croquis, exemples, références ou tout autre document.
                  Formats acceptés : PDF, PNG, JPG, TIFF, PSD, AI, INDD, DOCX, TXT, ZIP…
                </p>
                <FileUploader files={files} setFiles={setFiles} testId="creation-uploader" />
              </div>
            )}

            {step === 3 && (
              <div className="cs-card p-6 space-y-5" data-testid="step-format">
                <div className="cs-eyebrow text-neutral-500">Étape 4 · Format & structure</div>
                <h2 className="cs-h3 text-2xl">Dimensions et structure du support</h2>

                <div>
                  <div className="cs-eyebrow text-neutral-500">Dimensions du support à plat après découpe</div>
                  <p className="text-[12px] text-neutral-600 mt-1">Indiquez les dimensions du support déplié, après découpe et avant pliage.</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <label><span className="text-[11px] font-mono text-neutral-500">Largeur (mm)</span>
                      <input type="number" min="1" value={widthMm} onChange={(e) => setWidthMm(e.target.value)} disabled={dimUnknown} className="cs-input mt-1 w-full disabled:opacity-50" data-testid="width-mm" /></label>
                    <label><span className="text-[11px] font-mono text-neutral-500">Hauteur (mm)</span>
                      <input type="number" min="1" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} disabled={dimUnknown} className="cs-input mt-1 w-full disabled:opacity-50" data-testid="height-mm" /></label>
                  </div>
                  <label className="mt-2 inline-flex items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={dimUnknown} onChange={(e) => setDimUnknown(e.target.checked)} data-testid="dim-unknown" /> Je ne connais pas encore les dimensions
                  </label>
                </div>

                <div>
                  <div className="cs-eyebrow text-neutral-500">Structure du support</div>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" onClick={() => setRectoVerso(false)} data-testid="rv-recto"
                      className={`px-4 py-2 border ${!rectoVerso ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Recto uniquement</button>
                    <button type="button" onClick={() => setRectoVerso(true)} data-testid="rv-both"
                      className={`px-4 py-2 border ${rectoVerso ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Recto-verso</button>
                  </div>
                </div>

                <div>
                  <div className="cs-eyebrow text-neutral-500">Votre support est-il plié ?</div>
                  <div className="mt-3 flex gap-3">
                    <button type="button" onClick={() => setFolded(false)} data-testid="folded-no"
                      className={`px-4 py-2 border ${!folded ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Non</button>
                    <button type="button" onClick={() => setFolded(true)} data-testid="folded-yes"
                      className={`px-4 py-2 border ${folded ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Oui</button>
                  </div>
                </div>

                {folded && (
                  <div className="border border-black/10 bg-white/50 p-4 space-y-4" data-testid="fold-details">
                    <div>
                      <div className="cs-eyebrow text-neutral-500">Nombre de plis</div>
                      <div className="mt-2 flex gap-2">
                        {[1, 2, 3].map((n) => (
                          <button key={n} type="button" onClick={() => setFolds(n)} data-testid={`folds-${n}`}
                            className={`px-4 py-2 border ${folds === n ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>{n} pli{n > 1 ? "s" : ""}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="cs-eyebrow text-neutral-500">Type de pli</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {FOLD_TYPES.map((t) => (
                          <button key={t.key} type="button" onClick={() => setFoldType(t.key)} data-testid={`fold-type-${t.key}`}
                            className={`px-3 py-2 border text-[13px] ${foldType === t.key ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>{t.label}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="cs-eyebrow text-neutral-500">Format fermé souhaité (facultatif)</div>
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        <label><span className="text-[11px] font-mono text-neutral-500">Largeur (mm)</span>
                          <input type="number" min="1" value={closedW} onChange={(e) => setClosedW(e.target.value)} className="cs-input mt-1 w-full" data-testid="closed-w" /></label>
                        <label><span className="text-[11px] font-mono text-neutral-500">Hauteur (mm)</span>
                          <input type="number" min="1" value={closedH} onChange={(e) => setClosedH(e.target.value)} className="cs-input mt-1 w-full" data-testid="closed-h" /></label>
                      </div>
                      <p className="text-[11px] text-neutral-500 mt-2">Si vous ne le connaissez pas, Charbonier Studio pourra le déterminer à partir de votre projet et des spécifications de votre imprimeur.</p>
                    </div>
                  </div>
                )}

                <div>
                  <div className="cs-eyebrow text-neutral-500">À quoi votre création est-elle destinée ?</div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2">
                    {DESTINATIONS.map((d) => (
                      <button key={d.key} type="button" onClick={() => setDestination(d.key)} data-testid={`dest-${d.key}`}
                        className={`text-left px-4 py-3 border text-[14px] ${destination === d.key ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>{d.label}</button>
                    ))}
                  </div>
                  <p className="mt-2 text-[12px] text-[#0E7C90] font-mono uppercase tracking-[0.12em]" data-testid="dest-print-hint">
                    Charbonier Studio peut prendre en charge l'impression et la livraison à l'étape suivante.
                  </p>
                </div>

                <div>
                  <div className="cs-eyebrow text-neutral-500">Consignes de votre imprimeur (facultatif)</div>
                  <textarea rows={3} value={printerSpecs} onChange={(e) => setPrinterSpecs(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="printer-specs" placeholder="Copiez ici les consignes, ou joignez un fichier ci-dessous." />
                  <div className="mt-3">
                    <FileUploader files={specsFiles} setFiles={setSpecsFiles} testId="creation-specs-uploader" category="printer_specs" />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="cs-card p-6 space-y-5" data-testid="step-print">
                <div className="cs-eyebrow text-neutral-500">Étape 5 · Et pour l'impression ?</div>
                <h2 className="cs-h3 text-2xl">Souhaitez-vous également recevoir vos supports imprimés ?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { k: "files_only", t: "Je souhaite uniquement mes fichiers", d: "Vous recevez vos fichiers finalisés, prêts à être utilisés ou transmis à votre imprimeur." },
                    { k: "print_delivery", t: "Je souhaite recevoir les supports imprimés", d: "Charbonier Studio prend également en charge l'impression et la livraison." },
                  ].map((o) => (
                    <button key={o.k} type="button" onClick={() => setPrintOption(o.k)} data-testid={`print-option-${o.k}`}
                      className={`text-left border p-4 transition-colors ${printOption === o.k ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white hover:border-black/50"}`}>
                      <div className="font-serif text-lg">{o.t}</div>
                      <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed">{o.d}</p>
                    </button>
                  ))}
                </div>

                {printOption === "print_delivery" && (
                  <div className="border border-black/10 bg-white/50 p-4 space-y-4" data-testid="print-details">
                    <div>
                      <div className="cs-eyebrow text-neutral-500">Quantité souhaitée</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {PRINT_QUANTITIES.map((q) => (
                          <button key={q} type="button" onClick={() => setPrintQuantity(q)} data-testid={`print-qty-${q}`}
                            className={`px-4 py-2 border text-sm ${printQuantity === q ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>
                            {q === "autre" ? "Autre" : q === "1000" ? "1 000" : q}
                          </button>
                        ))}
                      </div>
                      {printQuantity === "autre" && (
                        <input value={printQuantityOther} onChange={(e) => setPrintQuantityOther(e.target.value)}
                          className="cs-input mt-3 w-full sm:w-64" placeholder="Précisez la quantité"
                          data-testid="print-qty-other" />
                      )}
                    </div>
                    <label className="block">
                      <span className="cs-eyebrow text-neutral-500">Avez-vous une exigence particulière ?</span>
                      <textarea rows={3} value={printRequirements} onChange={(e) => setPrintRequirements(e.target.value)}
                        className="cs-input mt-2 w-full resize-none" data-testid="print-requirements"
                        placeholder="Facultatif — papier, finition, délai souhaité, adresse de livraison si connue…" />
                    </label>
                  </div>
                )}

                <div className="border-t border-black/10 pt-4 text-[13px] text-neutral-600 leading-relaxed">
                  Le tarif d'impression et de livraison vous sera communiqué après vérification. Aucune impression ne sera lancée sans votre validation.
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="cs-card p-6" data-testid="step-recap">
                <div className="cs-eyebrow text-neutral-500">Étape 6 · Récapitulatif</div>
                <h2 className="cs-h3 mt-2 text-2xl">Votre création</h2>
                <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Support</dt><dd>{support === "Autre" ? supportOther || "Autre" : support}</dd></div>
                  <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Dimensions à plat</dt><dd>{dimUnknown ? "À déterminer" : `${widthMm || "?"} × ${heightMm || "?"} mm`}</dd></div>
                  <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Impression</dt><dd>{rectoVerso ? "Recto-verso" : "Recto uniquement"}</dd></div>
                  <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Structure</dt><dd>{folded ? `${folds} pli${folds > 1 ? "s" : ""} — ${FOLD_TYPES.find((t) => t.key === foldType)?.label || foldType}` : "Non plié"}</dd></div>
                  {folded && (closedW || closedH) && (
                    <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Format fermé</dt><dd>{closedW || "?"} × {closedH || "?"} mm</dd></div>
                  )}
                  <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Volets graphiques</dt><dd>{faces}</dd></div>
                  <div><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Destination</dt><dd>{DESTINATIONS.find((d) => d.key === destination)?.label}</dd></div>
                  <div className="sm:col-span-2"><dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">Impression & livraison</dt>
                    <dd>{printOption === "files_only"
                      ? "Fichiers uniquement"
                      : `Impression & livraison — quantité : ${printQuantity === "autre" ? (printQuantityOther || "à préciser") : printQuantity === "1000" ? "1 000" : printQuantity}`}
                    </dd>
                  </div>
                </dl>
                <div className="mt-6 border-t border-black/15 pt-4 text-sm space-y-1">
                  <div className="flex justify-between"><span>Premier volet</span><span className="font-mono">{eur(BASE_PRICE)}</span></div>
                  {faces > 1 && (
                    <div className="flex justify-between"><span>{faces - 1} volet{faces - 1 > 1 ? "s" : ""} supplémentaire{faces - 1 > 1 ? "s" : ""}</span><span className="font-mono">{eur((faces - 1) * EXTRA_PRICE)}</span></div>
                  )}
                  <div className="flex justify-between font-serif text-xl border-t border-black/10 pt-2 mt-2"><span>Total</span><span>{eur(price)}</span></div>
                </div>
                <div className="mt-6">
                  <div className="cs-eyebrow text-neutral-500">Inclus</div>
                  <ul className="mt-2 space-y-1.5 text-[14px]">
                    {["Création graphique originale", "Préparation du fichier", "Fonds perdus si nécessaires", "Fichier prêt imprimeur", "Échanges par messagerie", "2 séries de modifications mineures regroupées"].map((x, i) => (
                      <li key={i} className="flex items-start gap-2"><Check size={16} className="text-[#0E7C90] mt-0.5" />{x}</li>
                    ))}
                  </ul>
                </div>
                <p className="mt-6 text-[13px] text-neutral-700 leading-relaxed">
                  Afin de travailler efficacement sur votre projet, nous vous invitons à regrouper autant que possible
                  vos remarques dans un même message. Aucun supplément n'est engagé sans votre accord.
                </p>
              </div>
            )}

            {step === 6 && (
              <div className="cs-card p-6 space-y-4" data-testid="step-confirm">
                <div className="cs-eyebrow text-neutral-500">Étape 7 · Confirmation</div>
                <h2 className="cs-h3 text-2xl">Prêt à envoyer votre demande ?</h2>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  En confirmant, votre demande est transmise à Charbonier Studio. Vous serez notifié par email
                  et retrouverez le suivi de votre commande dans votre espace client. Aucun paiement n'est déclenché maintenant.
                </p>
                <button onClick={submit} disabled={submitting} className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="confirm-submit">
                  {submitting ? "Envoi en cours…" : `Confirmer et envoyer — ${eur(price)}`}
                </button>
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                data-testid="step-prev"
                className="inline-flex items-center gap-2 px-4 py-3 border border-black/30 hover:bg-black hover:text-[#FAF8F5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm">
                <ArrowLeft size={14} /> Précédent
              </button>
              {step < STEPS.length - 1 && (
                <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
                  data-testid="step-next"
                  className="inline-flex items-center gap-2 px-4 py-3 bg-black text-[#FAF8F5] hover:bg-[#0E7C90] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm">
                  Suivant <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:sticky lg:top-4 h-fit">
            <div className="cs-card p-5" data-testid="live-price">
              <div className="cs-eyebrow text-neutral-500">Prix en cours</div>
              <div className="mt-3 flex items-end justify-between">
                <span className="font-serif text-4xl">{eur(price)}</span>
                <span className="font-mono text-[11px] text-neutral-500">{faces} volet{faces > 1 ? "s" : ""}</span>
              </div>
              <div className="mt-3 text-[12px] text-neutral-600 leading-relaxed">
                Premier volet {eur(BASE_PRICE)} · Chaque volet supplémentaire +{EXTRA_PRICE} €.
                Le tarif dépend de la structure du support, pas de la difficulté artistique du visuel.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
